import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaTimes, FaPaperclip, FaSmile, FaMicrophone, FaArrowUp, FaHome, FaComments, FaChevronLeft, FaChevronDown, FaExternalLinkAlt, FaCheck } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { Utils, fetchWithAuth, getMarketingUrl } from '../../utils';
import './SupportChat.css';

const getFileUrl = (attachmentUrl) => {
  const baseUrl = Utils.baseurl.replace('/api/', '').replace(/\/$/, '');
  return baseUrl + attachmentUrl;
};

const SupportChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeView, setActiveView] = useState('home'); // 'home', 'messages', 'chat'
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const emojis = ['😀', '😂', '😊', '😍', '👍', '❤️'];

  const userData = JSON.parse(localStorage.getItem('userData') || '{}');

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = useCallback(async () => {
    try {
      const response = await fetchWithAuth(Utils.baseurl + 'support/conversations', {
        method: 'POST',
        body: JSON.stringify({ userId: userData.id }),
      });
      const result = await response.json();
      if (result.status) {
        setConversations(result.data || []);
        const unread = result.data?.filter(c => c.unread_count > 0).length || 0;
        setUnreadCount(unread);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  }, [userData.id]);

  useEffect(() => {
    if (isOpen) {
      fetchConversations();
    }
  }, [isOpen, fetchConversations]);

  useEffect(() => {
    const handleOpenChat = () => {
      setIsOpen(true);
    };
    window.addEventListener('openSupportChat', handleOpenChat);
    return () => {
      window.removeEventListener('openSupportChat', handleOpenChat);
    };
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMessages = async (conversationId) => {
    try {
      const response = await fetchWithAuth(Utils.baseurl + 'support/messages', {
        method: 'POST',
        body: JSON.stringify({ conversationId }),
      });
      const result = await response.json();
      if (result.status) {
        setMessages(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const startNewConversation = async () => {
    setActiveView('chat');
    setActiveConversation(null);
    setMessages([{
      id: 'welcome',
      message: 'Hello! Please provide your detailed questions with links and screenshots if possible. We will assist ASAP! If you leave, you will be emailed a response.',
      sender_type: 'admin',
      created_at: new Date().toISOString()
    }]);
  };

  const openConversation = async (conversation) => {
    setActiveConversation(conversation);
    setActiveView('chat');
    await fetchMessages(conversation.id);
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    const messageData = {
      userId: userData.id,
      message: newMessage,
      conversationId: activeConversation?.id || null,
    };

    // Optimistically add message to UI
    const tempMessage = {
      id: Date.now(),
      message: newMessage,
      sender_type: 'user',
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempMessage]);
    setNewMessage('');

    try {
      const response = await fetchWithAuth(Utils.baseurl + 'support/send-message', {
        method: 'POST',
        body: JSON.stringify(messageData),
      });
      const result = await response.json();
      if (result.status && result.conversationId) {
        setActiveConversation({ id: result.conversationId });
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // File attachment handler
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('userId', userData.id);
    formData.append('conversationId', activeConversation?.id || '');

    // Show file in chat optimistically
    const tempMessage = {
      id: Date.now(),
      message: file.name,
      messageType: 'attachment',
      sender_type: 'user',
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempMessage]);

    try {
      const response = await fetchWithAuth(Utils.baseurl + 'support/upload-attachment', {
        method: 'POST',
        body: formData,
      });
      const result = await response.json();
      if (result.status && result.conversationId) {
        setActiveConversation({ id: result.conversationId });
      }
    } catch (error) {
      console.error('Error uploading file:', error);
    }
    e.target.value = '';
  };

  // Emoji picker handler
  const handleEmojiSelect = (emoji) => {
    setNewMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  // Voice recording handlers
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        audioChunksRef.current.push(e.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await sendVoiceMessage(audioBlob);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const sendVoiceMessage = async (audioBlob) => {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'voice-message.webm');
    formData.append('userId', userData.id);
    formData.append('conversationId', activeConversation?.id || '');

    const tempMessage = {
      id: Date.now(),
      message: 'Voice message',
      messageType: 'voice',
      sender_type: 'user',
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempMessage]);

    try {
      const response = await fetchWithAuth(Utils.baseurl + 'support/upload-voice', {
        method: 'POST',
        body: formData,
      });
      const result = await response.json();
      if (result.status && result.conversationId) {
        setActiveConversation({ id: result.conversationId });
      }
    } catch (error) {
      console.error('Error sending voice message:', error);
    }
  };

  const quickLinks = [
    { name: 'Contact Sales', url: '#' },
    { name: 'Release Notes', url: '#' },
    { name: 'Quickstart guide', url: getMarketingUrl('/docs/firebase-credentials') },
    { name: 'Developer Guides', url: '#' },
    { name: 'API Reference', url: '#' },
  ];

  return (
    <div className="support-chat-container">
      {/* Chat Widget */}
      {isOpen && (
        <div className="support-chat-widget">
          {/* Home View */}
          {activeView === 'home' && (
            <div className="support-home">
              <div className="support-header">
                <div className="support-header-content">
                  <div className="support-logo">
                    <span className="logo-icon">N</span>
                    <span className="logo-text">Notify</span>
                  </div>
                  <div className="support-avatars">
                    <div className="avatar">J</div>
                    <div className="avatar">I</div>
                  </div>
                </div>
                <button className="close-btn" onClick={() => setIsOpen(false)}>
                  <FaTimes />
                </button>
                <div className="support-greeting">
                  <h2>Hi there 👋</h2>
                  <p>How can we help?</p>
                </div>
              </div>

              <div className="support-body">
                <button className="send-message-btn" onClick={startNewConversation}>
                  <span>Send us a message</span>
                  <FaArrowUp className="arrow-icon" />
                </button>

                <div className="status-card">
                  <div className="status-icon"><FaCheck /></div>
                  <div className="status-info">
                    <strong>Notify</strong>
                    <p>We're fully operational.</p>
                  </div>
                </div>

                <button className="subscribe-btn">Subscribe to updates</button>

                <div className="quick-links">
                  {quickLinks.map((link, index) => (
                    <a key={index} href={link.url} className="quick-link" target="_blank" rel="noopener noreferrer">
                      <span>{link.name}</span>
                      <FaExternalLinkAlt />
                    </a>
                  ))}
                </div>
              </div>

              <div className="support-footer">
                <button
                  className={`footer-tab ${activeView === 'home' ? 'active' : ''}`}
                  onClick={() => setActiveView('home')}
                >
                  <FaHome />
                  <span>Home</span>
                </button>
                <button
                  className={`footer-tab ${activeView === 'messages' ? 'active' : ''}`}
                  onClick={() => { setActiveView('messages'); fetchConversations(); }}
                >
                  <FaComments />
                  <span>Messages</span>
                  {unreadCount > 0 && <span className="unread-badge">{unreadCount}</span>}
                </button>
              </div>
            </div>
          )}

          {/* Messages List View */}
          {activeView === 'messages' && (
            <div className="support-messages-list">
              <div className="messages-header">
                <h3>Messages</h3>
                <button className="close-btn" onClick={() => setIsOpen(false)}>
                  <FaTimes />
                </button>
              </div>

              <div className="conversations-list">
                {conversations.length === 0 ? (
                  <div className="no-conversations">
                    <p>No conversations yet</p>
                    <button onClick={startNewConversation}>Start a conversation</button>
                  </div>
                ) : (
                  conversations.map((conv) => (
                    <div
                      key={conv.id}
                      className="conversation-item"
                      onClick={() => openConversation(conv)}
                    >
                      <div className="conv-avatar">N</div>
                      <div className="conv-info">
                        <strong>{conv.subject || 'Support Conversation'}</strong>
                        <p>{conv.last_message || 'No messages yet'}</p>
                      </div>
                      {conv.unread_count > 0 && (
                        <span className="conv-unread">{conv.unread_count}</span>
                      )}
                    </div>
                  ))
                )}
              </div>

              <div className="support-footer">
                <button
                  className="footer-tab"
                  onClick={() => setActiveView('home')}
                >
                  <FaHome />
                  <span>Home</span>
                </button>
                <button className="footer-tab active">
                  <FaComments />
                  <span>Messages</span>
                  {unreadCount > 0 && <span className="unread-badge">{unreadCount}</span>}
                </button>
              </div>
            </div>
          )}

          {/* Chat View */}
          {activeView === 'chat' && (
            <div className="support-chat-view">
              <div className="chat-header">
                <button className="back-btn" onClick={() => setActiveView('home')}>
                  <FaChevronLeft />
                </button>
                <div className="chat-header-info">
                  <div className="chat-avatars">
                    <div className="avatar small">I</div>
                    <div className="avatar small">J</div>
                  </div>
                  <span className="chat-title">Notify</span>
                </div>
                <div className="chat-header-actions">
                  <button className="more-btn">•••</button>
                  <button className="close-btn" onClick={() => setIsOpen(false)}>
                    <FaTimes />
                  </button>
                </div>
              </div>

              <div className="chat-messages">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`message ${msg.sender_type === 'user' ? 'user-message' : 'admin-message'}`}
                  >
                    {msg.attachment_url ? (
                      <div className="message-attachment">
                        {msg.attachment_url.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                          <a href={getFileUrl(msg.attachment_url)} target="_blank" rel="noopener noreferrer">
                            <img
                              src={getFileUrl(msg.attachment_url)}
                              alt={msg.attachment_name || 'Attachment'}
                            />
                          </a>
                        ) : msg.attachment_url.match(/\.(webm|mp3|wav)$/i) ? (
                          <div className="voice-msg">
                            <FaMicrophone />
                            <audio controls src={getFileUrl(msg.attachment_url)} />
                          </div>
                        ) : (
                          <a href={getFileUrl(msg.attachment_url)} target="_blank" rel="noopener noreferrer">
                            <FaPaperclip style={{ marginRight: '6px' }} />
                            {msg.attachment_name || 'Download'}
                          </a>
                        )}
                      </div>
                    ) : (
                      <p>
                        {(msg.messageType === 'attachment' || (typeof msg.message === 'string' && msg.message.startsWith('Attachment:'))) && (
                          <FaPaperclip style={{ marginRight: '6px' }} />
                        )}
                        {(msg.messageType === 'voice' || msg.message === 'Voice message') && (
                          <FaMicrophone style={{ marginRight: '6px' }} />
                        )}
                        {msg.message}
                      </p>
                    )}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              <div className="chat-input-container">
                <div className="chat-input-wrapper">
                  <textarea
                    placeholder="Message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    rows={1}
                  />
                  <div className="input-actions">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                      accept="image/*,.pdf,.doc,.docx,.txt"
                    />
                    <button className="action-btn" onClick={() => fileInputRef.current?.click()}>
                      <FaPaperclip />
                    </button>
                    <div className="emoji-picker-container">
                      <button className="action-btn" onClick={() => setShowEmojiPicker(!showEmojiPicker)}>
                        <FaSmile />
                      </button>
                      {showEmojiPicker && (
                        <div className="emoji-picker">
                          {emojis.map((emoji, index) => (
                            <button key={index} onClick={() => handleEmojiSelect(emoji)}>{emoji}</button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      className={`action-btn ${isRecording ? 'recording' : ''}`}
                      onClick={isRecording ? stopRecording : startRecording}
                    >
                      <FaMicrophone />
                    </button>
                  </div>
                  <button
                    className={`send-btn ${newMessage.trim() ? 'active' : ''}`}
                    onClick={sendMessage}
                    disabled={!newMessage.trim()}
                  >
                    <FaArrowUp />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Toggle Button */}
      <button
        className={`support-toggle-btn ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? <FaChevronDown /> : (
          <>
            <FaComments />
            {unreadCount > 0 && <span className="toggle-badge">{unreadCount}</span>}
          </>
        )}
      </button>
    </div>
  );
};

export default SupportChat;
